import {
  createFileRoute,
  useRouteContext,
  useRouter,
  useSearch,
} from "@tanstack/react-router";
import { graphql } from "@repo/graphql-types";
import { z } from "zod";
import { Plus } from "@repo/icons";
import { HomeNextSchedulesSectionQueryDocument, HomeSectionLivancePayQueryDocument, HomeSectionOneLivQueryDocument } from "@repo/graphql-types/graphql";
import { Page } from "@/components/page";
import { HomeProfileHeader } from "@/components/home-profile-header";
import { useGraphQL, useInvalidateQuery } from "@/hooks/use-graphql";
import { AsyncDataWrapper } from "@/components/async-data-wrapper";
import { BottomBarV2 } from "@/components/bottom-bar-v2.tsx";
import { Fab } from "@/components/fab";
import { FullScreenModalHome } from "@/components/home/full-screen-modal-home";
import { HomeNextSchedulesSection } from "@/components/home/home-next-schedules-section";
import { HomeSectionLivancePay } from "@/components/home/home-section-livance-pay";
import { HomeSectionOneLiv } from "@/components/home/home-section-oneliv";
import { HomeSectionLivanceAssist } from "@/components/home/home-section-livance-assist.tsx";

const HomePageQuery = graphql(`
  query HomePage($userId: Int!) {
    ...HomeProfileHeaderFragment
  }
`);

export const HomePage = (): JSX.Element => {
  const { user, flags } = useRouteContext({ strict: false });

  const pullToRefreshIsActive = flags["ativa-pull-to-refresh-na-home"];

  const queryResult = useGraphQL(HomePageQuery, {
    userId: user.codUsuario,
  });

  const { data } = queryResult;

  const router = useRouter();
  const searchParams = useSearch({
    from: "/home/",
  });

  const onClickFab = (): void => {
    void router.navigate({
      search: { action: "open-more-actions" },
    });
  };

  const onCloseModal = (): void => {
    router.history.back();
  };

  const isModalOpen = searchParams.action === "open-more-actions";

  const invalidateNextSchedulesQuery = useInvalidateQuery(
    HomeNextSchedulesSectionQueryDocument,
  );

  const invalidateLivancePayQuery = useInvalidateQuery(
    HomeSectionLivancePayQueryDocument,
  );
  const invalidateOneLivQuery = useInvalidateQuery(HomeSectionOneLivQueryDocument);

  const handleRefresh = async (): Promise<void> => {
    await queryResult.refetch();
    invalidateNextSchedulesQuery();
    invalidateLivancePayQuery();
    invalidateOneLivQuery();
  };

  return (
    <>
      <Page
        className="px-0 pt-0 bg-neutral-100"
        id="bottom-bar-v2-padding-bottom"
        onRefresh={pullToRefreshIsActive ? handleRefresh : undefined}
      >
        <AsyncDataWrapper {...queryResult}>
          {data ? (
            <>
              <div className="px-4 pt-4">
                <HomeProfileHeader data={data} />
              </div>
              <div className="py-6 flex flex-col gap-12">
                <div>
                  <HomeNextSchedulesSection />
                </div>
                <div className="px-4">
                  <HomeSectionLivancePay />
                </div>
                <div className="px-4">
                  <HomeSectionLivanceAssist />
                </div>
                <div className="px-4">
                  <HomeSectionOneLiv />
                </div>
              </div>
              <Fab onClick={onClickFab}>
                <Plus size={32} className="fill-white" />
              </Fab>
              <FullScreenModalHome isOpen={isModalOpen} onClose={onCloseModal} />
            </>
          ) : null}
        </AsyncDataWrapper>
      </Page>
      <BottomBarV2 />
    </>
  );
};

export const Route = createFileRoute("/home/")({
  component: HomePage,
  validateSearch: z.object({
    action: z.string().optional(),
  }),
});
